import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User, sendPasswordResetEmail, fetchSignInMethodsForEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs, addDoc, deleteDoc, writeBatch, onSnapshot, documentId, orderBy, limit } from 'firebase/firestore';

import type { AppointmentLogFields, AppointmentRecord, CallSheetItemRecord, CallSheetRecord, OutboundCallRecord, ProspectContactRecord, ProspectRecord } from '../types/prospecting';
import { formatDateKey, formatWeekOfLabel, nowIso, startOfWeekMonday } from '../types/prospecting';
import type { OrganizationKey } from '../types/organizations';
import { DEFAULT_ORGANIZATION, recordMatchesOrg, resolveOrgKey } from '../types/organizations';
import { filterRecordsByOrg } from '../utils/orgScope';

const withOrgKey = (orgKey?: OrganizationKey) => resolveOrgKey(orgKey);

const universalOrgDocId = (base: string, orgKey: OrganizationKey = DEFAULT_ORGANIZATION): string =>
  `${base}_${orgKey}`;

export type OperatingMode = 'gemmetric' | 'spectrum-reach';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// User management functions
export const createUser = async (email: string, password: string, name: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Check if this is the first user (SuperAdmin)
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);
    const isFirstUser = usersSnapshot.empty;
    
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      name: name,
      role: isFirstUser ? 'SuperAdmin' : 'user',
      createdAt: new Date(),
      isSuperAdmin: isFirstUser,
      hideInstructions: false
    });
    
    return user;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Send password reset email
export const resetPassword = async (email: string) => {
  try {
    // Use default Firebase continue URL to avoid unauthorized-continue-uri errors
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Check if an email is registered and supports password sign-in
export const getSignInMethods = async (email: string): Promise<string[]> => {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods || [];
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// User data management
export const getUserProfile = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    // Auto-provision minimal profile if missing
    const authUser = auth.currentUser;
    const fallbackName = authUser?.displayName || (authUser?.email || 'User');
    await setDoc(doc(db, 'users', userId), {
      email: authUser?.email || '',
      name: fallbackName,
      role: 'user',
      createdAt: new Date(),
      isSuperAdmin: false,
      hideInstructions: false
    }, { merge: true });
    const created = await getDoc(doc(db, 'users', userId));
    return created.exists() ? created.data() : null;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const updateUserRole = async (userId: string, role: string) => {
  try {
    await updateDoc(doc(db, 'users', userId), { role });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const makeUserSuperAdmin = async (userId: string) => {
  try {
    await updateDoc(doc(db, 'users', userId), { 
      role: 'SuperAdmin',
      isSuperAdmin: true 
    });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const updateUserPreferences = async (userId: string, preferences: { hideInstructions?: boolean }) => {
  try {
    await updateDoc(doc(db, 'users', userId), preferences);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const deleteUser = async (userId: string) => {
  try {
    // Delete user document from Firestore
    await deleteDoc(doc(db, 'users', userId));
    console.log('User document deleted from Firestore:', userId);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Admin-only: call Netlify admin-users function to suspend/unsuspend/hard delete Auth user
export const adminUpdateAuthUser = async (
  uid: string,
  action: 'suspend' | 'unsuspend' | 'hardDelete'
) => {
  const endpoint = (process.env.NODE_ENV === 'development')
    ? 'http://localhost:8888/.netlify/functions/admin-users'
    : '/.netlify/functions/admin-users';
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid, action })
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Admin action failed');
  }
  return res.json().catch(() => ({}));
};

// Re-authenticate the currently signed-in admin with their password
export const reauthenticateCurrentUser = async (password: string) => {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('Not authenticated');
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
};

// Prospect data management
export const saveProspects = async (
  userId: string,
  prospects: any[],
  searchParams: any,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  try {
    const operatingMode = searchParams?.operatingMode;
    if (operatingMode !== 'gemmetric' && operatingMode !== 'spectrum-reach') {
      throw new Error('Search operatingMode must be either "gemmetric" or "spectrum-reach"');
    }
    const searchId = `${userId}_${Date.now()}`;
    const scopedOrg = withOrgKey(orgKey);
    const createdAt =
      searchParams?.createdAt && !Number.isNaN(new Date(searchParams.createdAt).getTime())
        ? new Date(searchParams.createdAt)
        : new Date();
    await setDoc(doc(db, 'searches', searchId), {
      userId,
      orgKey: scopedOrg,
      operatingMode,
      prospects,
      searchParams: { ...searchParams, orgKey: scopedOrg, operatingMode },
      createdAt
    });
    return searchId;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getUserSearches = async (userId: string, orgKey?: OrganizationKey) => {
  try {
    const q = query(collection(db, 'searches'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const searches: any[] = [];
    querySnapshot.forEach((doc) => {
      searches.push({ id: doc.id, ...doc.data() });
    });
    const scoped = orgKey
      ? searches.filter((s) => recordMatchesOrg({ orgKey: s.orgKey || s.searchParams?.orgKey }, orgKey))
      : searches;
    return scoped.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const subscribeUserSearches = (
  userId: string,
  onChange: (searches: any[]) => void,
  onError?: (error: Error) => void,
  orgKey?: OrganizationKey
) => {
  const q = query(collection(db, 'searches'), where('userId', '==', userId));
  return onSnapshot(
    q,
    (querySnapshot) => {
      const searches: any[] = [];
      querySnapshot.forEach((doc) => {
        searches.push({ id: doc.id, ...doc.data() });
      });
      searches.sort((a, b) => {
        const aMs = a?.createdAt?.toDate?.()?.getTime?.() || new Date(a?.createdAt || 0).getTime() || 0;
        const bMs = b?.createdAt?.toDate?.()?.getTime?.() || new Date(b?.createdAt || 0).getTime() || 0;
        return bMs - aMs;
      });
      const scoped = orgKey
        ? searches.filter((s) => recordMatchesOrg({ orgKey: s.orgKey || s.searchParams?.orgKey }, orgKey))
        : searches;
      onChange(scoped);
    },
    (error) => {
      if (onError) onError(error as any);
    }
  );
};

export const deleteUserSearch = async (searchId: string) => {
  try {
    await deleteDoc(doc(db, 'searches', searchId));
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Call logs management
export const saveCallLog = async (
  userId: string,
  prospectId: string,
  callData: any,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  return withRateLimitProtection(async () => {
    return withRetryBackoff(async () => {
      const cleanCallData = Object.fromEntries(
        Object.entries(callData || {}).filter(([, value]) => value !== undefined)
      );
      const scopedOrg = withOrgKey((cleanCallData as any)?.orgKey || orgKey);
      const logRef = await addDoc(collection(db, 'callLogs'), {
        userId,
        prospectId,
        ...cleanCallData,
        orgKey: scopedOrg,
        createdAt: new Date((cleanCallData as any)?.timestamp || Date.now())
      });
      if ((cleanCallData as any)?.appointmentAt) {
        const appointmentPayload = Object.fromEntries(
          Object.entries({
            userId,
            orgKey: scopedOrg,
            prospectId,
            name: String((cleanCallData as any)?.name || prospectId),
            category: String((cleanCallData as any)?.category || 'Business'),
            phone: String((cleanCallData as any)?.phone || ''),
            website: String((cleanCallData as any)?.website || ''),
            address: String((cleanCallData as any)?.address || ''),
            note: typeof (cleanCallData as any)?.note === 'string' ? (cleanCallData as any).note : '',
            appointmentAt: String((cleanCallData as any).appointmentAt),
            durationMinutes: Number((cleanCallData as any)?.durationMinutes) || 30,
            appointmentType: (cleanCallData as any)?.appointmentType || 'phone',
            appointmentLocation: (cleanCallData as any)?.appointmentLocation || '',
            appointmentContactName: (cleanCallData as any)?.appointmentContactName || '',
            appointmentContactPhone: (cleanCallData as any)?.appointmentContactPhone || '',
            appointmentContactEmail: (cleanCallData as any)?.appointmentContactEmail || '',
            source: 'manual',
            status: 'scheduled',
            callLogId: logRef.id,
            createdAt: String((cleanCallData as any)?.timestamp || nowIso()),
            updatedAt: nowIso()
          }).filter(([, value]) => value !== undefined)
        ) as Omit<AppointmentRecord, 'id'>;
        await addDoc(
          collection(db, 'appointments'),
          appointmentPayload
        );
      }
    });
  });
};

export type AppointmentCallLog = {
  id: string;
  userId: string;
  prospectId: string;
  name: string;
  category: string;
  phone: string;
  website: string;
  address: string;
  timestamp: string;
  outcome: string;
  note?: string;
  summary?: string;
  transcript?: string;
  recordingUrl?: string;
  appointmentStatus?: 'scheduled' | 'completed' | 'cancelled';
  createdAt?: any;
} & Partial<AppointmentLogFields>;

export type CalendarAppointment = AppointmentRecord;

const isDateInIsoRange = (value: any, startIso: string, endIso: string): boolean => {
  if (!value) return false;
  const ts = new Date(typeof value === 'string' ? value : isoFromAny(value)).getTime();
  const startTs = new Date(startIso).getTime();
  const endTs = new Date(endIso).getTime();
  if (!Number.isFinite(ts) || !Number.isFinite(startTs) || !Number.isFinite(endTs)) return false;
  return ts >= startTs && ts <= endTs;
};

const isFirestoreIndexError = (error: any): boolean =>
  /index/i.test(String(error?.message || error || ''));

export const getUserCallLogs = async (userId: string, orgKey?: OrganizationKey) => {
  try {
    const q = query(collection(db, 'callLogs'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const logs: any[] = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    const scoped = orgKey ? filterRecordsByOrg(logs, orgKey) : logs;
    return scoped.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getProspectCallLogs = async (userId: string, prospectId: string) => {
  try {
    const q = query(
      collection(db, 'callLogs'),
      where('userId', '==', userId),
      where('prospectId', '==', prospectId)
    );
    const querySnapshot = await getDocs(q);
    const logs: any[] = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return logs.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getAppointmentLogsForRange = async (
  userId: string,
  startIso: string,
  endIso: string
): Promise<AppointmentCallLog[]> => {
  try {
    let querySnapshot;
    try {
      const q = query(
        collection(db, 'callLogs'),
        where('userId', '==', userId),
        where('appointmentAt', '>=', startIso),
        where('appointmentAt', '<=', endIso),
        orderBy('appointmentAt', 'asc' as any)
      );
      querySnapshot = await getDocs(q);
    } catch (error: any) {
      if (!isFirestoreIndexError(error)) throw error;
      querySnapshot = await getDocs(
        query(
          collection(db, 'callLogs'),
          where('userId', '==', userId)
        )
      );
    }
    const out: AppointmentCallLog[] = [];
    querySnapshot.forEach((d) => {
      const data: any = d.data();
      if (!isDateInIsoRange(data.appointmentAt, startIso, endIso)) return;
      out.push({
        ...(data as AppointmentCallLog),
        id: d.id
      });
    });
    return out.sort((a, b) => new Date(a.appointmentAt || 0).getTime() - new Date(b.appointmentAt || 0).getTime());
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getAppointmentsForRange = async (
  userId: string,
  startIso: string,
  endIso: string,
  orgKey?: OrganizationKey
): Promise<CalendarAppointment[]> => {
  try {
    const [appointmentRows, legacyRows] = await Promise.all([
      (async () => {
        try {
          return await getDocs(
            query(
              collection(db, 'appointments'),
              where('userId', '==', userId),
              where('appointmentAt', '>=', startIso),
              where('appointmentAt', '<=', endIso),
              orderBy('appointmentAt', 'asc' as any)
            )
          );
        } catch (error: any) {
          if (!isFirestoreIndexError(error)) throw error;
          return getDocs(
            query(
              collection(db, 'appointments'),
              where('userId', '==', userId)
            )
          );
        }
      })(),
      getAppointmentLogsForRange(userId, startIso, endIso)
    ]);

    const byKey = new Map<string, CalendarAppointment>();

    appointmentRows.forEach((d) => {
      const data: any = d.data();
      if (data.status === 'cancelled') return;
      if (!isDateInIsoRange(data.appointmentAt, startIso, endIso)) return;
      const row: CalendarAppointment = {
        ...(data as CalendarAppointment),
        id: d.id,
        createdAt: data.createdAt ? isoFromAny(data.createdAt) : nowIso(),
        updatedAt: data.updatedAt ? isoFromAny(data.updatedAt) : nowIso()
      };
      const key = row.callLogId || `${row.prospectId}__${row.appointmentAt}`;
      byKey.set(key, row);
    });

    legacyRows.forEach((row) => {
      const key = row.id || `${row.prospectId}__${row.appointmentAt}`;
      if (byKey.has(key)) return;
      if (!row.appointmentAt) return;
      if (row.appointmentStatus === 'cancelled') return;
      byKey.set(key, {
        id: row.id,
        userId: row.userId,
        prospectId: row.prospectId,
        name: row.name,
        category: row.category,
        phone: row.phone,
        website: row.website,
        address: row.address,
        note: row.note || '',
        appointmentAt: row.appointmentAt,
        durationMinutes: row.durationMinutes,
        appointmentType: row.appointmentType,
        appointmentLocation: row.appointmentLocation,
        appointmentContactName: row.appointmentContactName,
        appointmentContactPhone: row.appointmentContactPhone,
        appointmentContactEmail: row.appointmentContactEmail,
        source: 'call_log',
        status: 'scheduled',
        callLogId: row.id,
        createdAt: isoFromAny(row.createdAt || row.timestamp),
        updatedAt: isoFromAny(row.createdAt || row.timestamp)
      });
    });

    const rows = Array.from(byKey.values()).sort(
      (a, b) => new Date(a.appointmentAt).getTime() - new Date(b.appointmentAt).getTime()
    );
    return orgKey ? filterRecordsByOrg(rows, orgKey) : rows;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const cancelAppointment = async (userId: string, appointment: CalendarAppointment) => {
  const now = nowIso();
  if (appointment.source !== 'call_log') {
    await setDoc(
      doc(db, 'appointments', appointment.id),
      stripUndefined({
        userId,
        status: 'cancelled',
        updatedAt: now
      } as any),
      { merge: true }
    );
  }

  if (appointment.callLogId) {
    await setDoc(
      doc(db, 'callLogs', appointment.callLogId),
      stripUndefined({
        userId,
        appointmentStatus: 'cancelled',
        updatedAt: now
      } as any),
      { merge: true }
    );
  }
};

// Saved prospects management
export const saveProspect = async (userId: string, prospect: any) => {
  try {
    await addDoc(collection(db, 'savedProspects'), {
      userId,
      prospect,
      createdAt: new Date()
    });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getUserSavedProspects = async (userId: string) => {
  try {
    const q = query(collection(db, 'savedProspects'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const prospects: any[] = [];
    querySnapshot.forEach((doc) => {
      prospects.push({ id: doc.id, ...doc.data() });
    });
    return prospects.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// ===== PROSPECTING (SINGLE-USER CRM) =====

const isoFromAny = (v: any): string => {
  if (!v) return nowIso();
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  if (typeof v.toDate === 'function') return v.toDate().toISOString();
  return nowIso();
};

const stripUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((k) => {
    const v = (obj as any)[k];
    if (v !== undefined) out[k] = v;
  });
  return out as Partial<T>;
};

// Upsert a Prospect record (pre-sale). Doc id should be placeId preferred, else businessHash.
export const upsertProspect = async (
  userId: string,
  prospectId: string,
  patch: Partial<ProspectRecord>
) => {
  const ref = doc(db, 'prospects', prospectId);
  const createdAt = patch.createdAt || nowIso();
  const updatedAt = nowIso();
  const base: Partial<ProspectRecord> = {
    id: prospectId,
    userId,
    saved: Boolean((patch as any).saved ?? true),
    status: (patch.status as any) || 'saved',
    createdAt,
    updatedAt
  };
  await setDoc(ref, stripUndefined({ ...base, ...patch, updatedAt } as any), { merge: true });
  return prospectId;
};

export const setProspectSaved = async (
  userId: string,
  prospectId: string,
  saved: boolean
) => {
  const ref = doc(db, 'prospects', prospectId);
  await setDoc(ref, stripUndefined({ userId, saved, updatedAt: nowIso() } as any), { merge: true });
};

export const getProspect = async (prospectId: string): Promise<ProspectRecord | null> => {
  const snap = await getDoc(doc(db, 'prospects', prospectId));
  if (!snap.exists()) return null;
  const data: any = snap.data();
  return {
    ...(data as ProspectRecord),
    id: snap.id,
    createdAt: isoFromAny(data.createdAt),
    updatedAt: isoFromAny(data.updatedAt)
  };
};

export const getUserSavedProspectsV2 = async (userId: string, orgKey?: OrganizationKey): Promise<ProspectRecord[]> => {
  const q = query(
    collection(db, 'prospects'),
    where('userId', '==', userId),
    where('saved', '==', true)
  );
  const qs = await getDocs(q);
  const out: ProspectRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    out.push({
      ...(data as ProspectRecord),
      id: d.id,
      createdAt: isoFromAny(data.createdAt),
      updatedAt: isoFromAny(data.updatedAt)
    });
  });
  const scoped = orgKey ? filterRecordsByOrg(out, orgKey) : out;
  return scoped.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};

export const getDueProspectReminders = async (
  userId: string,
  asOfIso: string = nowIso(),
  orgKey?: OrganizationKey
): Promise<ProspectRecord[]> => {
  const q = query(collection(db, 'prospects'), where('userId', '==', userId));
  const qs = await getDocs(q);
  const out: ProspectRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    const nextReminderAt = data?.nextReminderAt;
    if (!nextReminderAt || typeof nextReminderAt !== 'string') return;
    const due = new Date(nextReminderAt).getTime() <= new Date(asOfIso).getTime();
    if (!due) return;
    out.push({
      ...(data as ProspectRecord),
      id: d.id,
      createdAt: isoFromAny(data.createdAt),
      updatedAt: isoFromAny(data.updatedAt)
    });
  });
  const scoped = orgKey ? filterRecordsByOrg(out, orgKey) : out;
  return scoped.sort((a, b) => {
    const at = new Date(a.nextReminderAt || 0).getTime();
    const bt = new Date(b.nextReminderAt || 0).getTime();
    return at - bt;
  });
};

// Contacts (separate collection for query simplicity)
export const upsertProspectContact = async (
  userId: string,
  contactId: string,
  patch: Partial<ProspectContactRecord>
) => {
  const ref = doc(db, 'prospectContacts', contactId);
  const createdAt = patch.createdAt || nowIso();
  const updatedAt = nowIso();
  await setDoc(ref, stripUndefined({ ...patch, userId, createdAt, updatedAt } as any), { merge: true });
  return contactId;
};

export const getProspectContacts = async (
  userId: string,
  prospectId: string
): Promise<ProspectContactRecord[]> => {
  const q = query(
    collection(db, 'prospectContacts'),
    where('userId', '==', userId),
    where('prospectId', '==', prospectId)
  );
  const qs = await getDocs(q);
  const out: ProspectContactRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    out.push({
      ...(data as ProspectContactRecord),
      id: d.id,
      createdAt: isoFromAny(data.createdAt),
      updatedAt: isoFromAny(data.updatedAt)
    });
  });
  return out.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};

export const setProspectBestContact = async (
  userId: string,
  prospectId: string,
  bestContactId: string | null
) => {
  const ref = doc(db, 'prospects', prospectId);
  await setDoc(ref, stripUndefined({ userId, bestContactId: bestContactId || null, updatedAt: nowIso() } as any), { merge: true });
};

export const deleteProspectContact = async (
  userId: string,
  contactId: string
) => {
  const ref = doc(db, 'prospectContacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data: any = snap.data();
  if (data?.userId !== userId) {
    throw new Error('Unauthorized to delete this contact');
  }
  await deleteDoc(ref);
};

// Call sheets
export const createCallSheet = async (
  userId: string,
  name: string,
  dateKey: string,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
): Promise<string> => {
  const res = await addDoc(collection(db, 'callSheets'), stripUndefined({
    userId,
    orgKey: withOrgKey(orgKey),
    name,
    dateKey,
    createdAt: nowIso(),
    updatedAt: nowIso()
  } as any));
  return res.id;
};

export const getOrCreateTodayCallSheet = async (
  userId: string,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
): Promise<CallSheetRecord> => {
  const weekStart = startOfWeekMonday(new Date());
  return getOrCreateCallSheetForWeek(userId, weekStart, orgKey);
};

export const getOrCreateCallSheetForWeek = async (
  userId: string,
  weekDate: Date,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
): Promise<CallSheetRecord> => {
  const weekStart = startOfWeekMonday(weekDate);
  const weekStartKey = formatDateKey(weekStart);
  const weekLabel = formatWeekOfLabel(weekStart);
  const q = query(
    collection(db, 'callSheets'),
    where('userId', '==', userId),
    where('dateKey', '==', weekStartKey),
    orderBy('updatedAt', 'desc' as any),
    limit(1)
  );
  const qs = await getDocs(q);
  const scopedDocs = qs.docs.filter((d) => recordMatchesOrg(d.data() as any, orgKey));
  if (scopedDocs.length > 0) {
    const preferred =
      scopedDocs.find((d) => {
        const data: any = d.data();
        const name = String(data?.name || '').trim().toLowerCase();
        return name === 'today' || name === weekLabel.toLowerCase();
      }) || scopedDocs[0];
    const data: any = preferred.data();
    return {
      ...(data as CallSheetRecord),
      id: preferred.id,
      createdAt: isoFromAny(data.createdAt),
      updatedAt: isoFromAny(data.updatedAt)
    };
  }
  const id = await createCallSheet(userId, weekLabel, weekStartKey, orgKey);
  const created = await getDoc(doc(db, 'callSheets', id));
  const data: any = created.data();
  return {
    ...(data as CallSheetRecord),
    id,
    createdAt: isoFromAny(data?.createdAt),
    updatedAt: isoFromAny(data?.updatedAt)
  };
};

export const listCallSheets = async (userId: string, orgKey?: OrganizationKey): Promise<CallSheetRecord[]> => {
  const q = query(collection(db, 'callSheets'), where('userId', '==', userId));
  const qs = await getDocs(q);
  const out: CallSheetRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    out.push({
      ...(data as CallSheetRecord),
      id: d.id,
      createdAt: isoFromAny(data.createdAt),
      updatedAt: isoFromAny(data.updatedAt)
    });
  });
  const scoped = orgKey ? filterRecordsByOrg(out, orgKey) : out;
  return scoped.sort((a, b) => {
    const aAuto = /^today$/i.test(a.name) || /^week of /i.test(a.name);
    const bAuto = /^today$/i.test(b.name) || /^week of /i.test(b.name);
    if (aAuto && !bAuto) return -1;
    if (bAuto && !aAuto) return 1;
    return b.dateKey.localeCompare(a.dateKey);
  });
};

export const addProspectToCallSheet = async (
  userId: string,
  sheetId: string,
  prospectId: string,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
): Promise<string> => {
  // Determine next order index (cheap approach; OK for single-user volumes)
  const q = query(
    collection(db, 'callSheetItems'),
    where('userId', '==', userId),
    where('sheetId', '==', sheetId),
    orderBy('order', 'desc' as any),
    limit(1)
  );
  const qs = await getDocs(q);
  const nextOrder = qs.empty ? 0 : (Number(qs.docs[0].data()?.order) + 1);

  // Prevent duplicates for same prospectId in same sheet
  const dupQ = query(
    collection(db, 'callSheetItems'),
    where('userId', '==', userId),
    where('sheetId', '==', sheetId),
    where('prospectId', '==', prospectId),
    limit(1)
  );
  const dup = await getDocs(dupQ);
  if (!dup.empty) return dup.docs[0].id;

  const res = await addDoc(collection(db, 'callSheetItems'), stripUndefined({
    userId,
    orgKey: withOrgKey(orgKey),
    sheetId,
    prospectId,
    order: nextOrder,
    addedAt: nowIso()
  } as any));

  await setDoc(doc(db, 'callSheets', sheetId), stripUndefined({ userId, updatedAt: nowIso() } as any), { merge: true });
  return res.id;
};

export const listCallSheetItems = async (
  userId: string,
  sheetId: string
): Promise<CallSheetItemRecord[]> => {
  const q = query(
    collection(db, 'callSheetItems'),
    where('userId', '==', userId),
    where('sheetId', '==', sheetId),
    orderBy('order', 'asc' as any)
  );
  const qs = await getDocs(q);
  const out: CallSheetItemRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    out.push({
      ...(data as CallSheetItemRecord),
      id: d.id,
      addedAt: isoFromAny(data.addedAt)
    });
  });
  return out;
};

export const subscribeCallSheetItems = (
  userId: string,
  sheetId: string,
  onChange: (items: CallSheetItemRecord[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(
    collection(db, 'callSheetItems'),
    where('userId', '==', userId),
    where('sheetId', '==', sheetId),
    orderBy('order', 'asc' as any)
  );
  return onSnapshot(
    q,
    (qs) => {
      const out: CallSheetItemRecord[] = [];
      qs.forEach((d) => {
        const data: any = d.data();
        out.push({
          ...(data as CallSheetItemRecord),
          id: d.id,
          addedAt: isoFromAny(data.addedAt)
        });
      });
      onChange(out);
    },
    (error) => {
      if (onError) onError(error as any);
    }
  );
};

export const removeCallSheetItem = async (itemId: string) => {
  await deleteDoc(doc(db, 'callSheetItems', itemId));
};

export const updateCallSheetItemState = async (
  itemId: string,
  patch: Partial<Pick<CallSheetItemRecord, 'lastOutcome' | 'lastCalledAt' | 'queueState'>>
) => {
  await setDoc(doc(db, 'callSheetItems', itemId), stripUndefined(patch as any), { merge: true });
};

export const listOutboundCallsForSheet = async (
  userId: string,
  callSheetId: string
): Promise<OutboundCallRecord[]> => {
  const q = query(
    collection(db, 'outboundCalls'),
    where('userId', '==', userId),
    where('callSheetId', '==', callSheetId)
  );
  const qs = await getDocs(q);
  const out: OutboundCallRecord[] = [];
  qs.forEach((d) => {
    const data: any = d.data();
    out.push({
      ...(data as OutboundCallRecord),
      id: d.id,
      createdAt: isoFromAny(data.createdAt),
      startedAt: data.startedAt ? isoFromAny(data.startedAt) : undefined,
      endedAt: data.endedAt ? isoFromAny(data.endedAt) : undefined
    });
  });
  return out.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const subscribeOutboundCallsForSheet = (
  userId: string,
  callSheetId: string,
  onChange: (calls: OutboundCallRecord[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(
    collection(db, 'outboundCalls'),
    where('userId', '==', userId),
    where('callSheetId', '==', callSheetId)
  );
  return onSnapshot(
    q,
    (qs) => {
      const out: OutboundCallRecord[] = [];
      qs.forEach((d) => {
        const data: any = d.data();
        out.push({
          ...(data as OutboundCallRecord),
          id: d.id,
          createdAt: isoFromAny(data.createdAt),
          startedAt: data.startedAt ? isoFromAny(data.startedAt) : undefined,
          endedAt: data.endedAt ? isoFromAny(data.endedAt) : undefined
        });
      });
      onChange(out.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    },
    (error) => {
      if (onError) onError(error as any);
    }
  );
};


export const removeSavedProspect = async (prospectId: string) => {
  try {
    await deleteDoc(doc(db, 'savedProspects', prospectId));
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Remove a saved prospect by user and prospect business id
export const removeSavedProspectByUserAndBusiness = async (userId: string, businessId: string) => {
  try {
    const q = query(collection(db, 'savedProspects'), where('userId', '==', userId));
    const qs = await getDocs(q);
    const matches: string[] = [];
    qs.forEach(d => {
      const data: any = d.data();
      if (data?.prospect?.id === businessId) {
        matches.push(d.id);
      }
    });
    if (matches.length > 0) {
      await deleteDoc(doc(db, 'savedProspects', matches[0]));
    }
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Clear all saved prospects for a user
export const clearUserSavedProspects = async (userId: string) => {
  try {
    const q = query(collection(db, 'savedProspects'), where('userId', '==', userId));
    const qs = await getDocs(q);
    const batch = writeBatch(db);
    qs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Clear all call logs for a user
export const clearUserCallLogs = async (userId: string) => {
  try {
    const q = query(collection(db, 'callLogs'), where('userId', '==', userId));
    const qs = await getDocs(q);
    const batch = writeBatch(db);
    qs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Admin functions
export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const users: any[] = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return users;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Admin functions
export const clearAICache = async () => {
  try {
    // Clear AI cache from Firestore (if we had it stored there)
    // For now, this is a placeholder for future implementation
    console.log('AI cache cleared (Firebase implementation pending)');
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const saveBlocklist = async (userId: string, blocklist: string[]) => {
  try {
    await setDoc(doc(db, 'blocklists', userId), {
      blocklist,
      updatedAt: new Date()
    });
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getBlocklist = async (userId: string) => {
  try {
    const blocklistDoc = await getDoc(doc(db, 'blocklists', userId));
    if (blocklistDoc.exists()) {
      return blocklistDoc.data().blocklist || [];
    }
    return [];
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// ===== UNIVERSAL BLOCKLIST MANAGEMENT =====
// Store blocklist in a single document that all users share
// Only admins can read/write, but all users benefit from filtering
export const saveUniversalBlocklist = async (
  blocklist: string[],
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  return withRateLimitProtection(async () => {
    return withRetryBackoff(async () => {
      await setDoc(doc(db, 'universal', universalOrgDocId('blocklist', orgKey)), {
        blocklist,
        orgKey,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.uid || 'unknown'
      });
      console.log(`✅ Org blocklist saved (${orgKey}):`, blocklist.length, 'items');
    });
  });
};

export const getUniversalBlocklist = async (orgKey: OrganizationKey = DEFAULT_ORGANIZATION) => {
  try {
    const scopedDoc = await getDoc(doc(db, 'universal', universalOrgDocId('blocklist', orgKey)));
    if (scopedDoc.exists()) {
      return scopedDoc.data().blocklist || [];
    }
    if (orgKey === DEFAULT_ORGANIZATION) {
      const legacyDoc = await getDoc(doc(db, 'universal', 'blocklist'));
      if (legacyDoc.exists()) {
        return legacyDoc.data().blocklist || [];
      }
    }
    return [];
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Subscribe to org-scoped blocklist changes in real-time
export const subscribeUniversalBlocklist = (
  onChange: (entries: string[]) => void,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  const ref = doc(db, 'universal', universalOrgDocId('blocklist', orgKey));
  let legacyLoaded = false;
  return onSnapshot(ref, async (snap) => {
    const data = snap.data() as any;
    let list = Array.isArray(data?.blocklist) ? data.blocklist : [];
    if (!list.length && orgKey === DEFAULT_ORGANIZATION && !legacyLoaded) {
      legacyLoaded = true;
      try {
        const legacyDoc = await getDoc(doc(db, 'universal', 'blocklist'));
        if (legacyDoc.exists()) {
          list = legacyDoc.data().blocklist || [];
        }
      } catch {
        list = [];
      }
    }
    onChange(list);
  }, (error) => {
    console.error('Blocklist snapshot error:', error);
  });
};

// ===== UNIVERSAL AI CACHE MANAGEMENT =====
// Store business data cache in Firebase for sharing across users
// All users can read/write to benefit from shared business data

export const saveUniversalAICache = async (businessData: any) => {
  return withRateLimitProtection(async () => {
    return withRetryBackoff(async () => {
      await performSaveUniversalAICache(businessData);
    });
  });
};

const performSaveUniversalAICache = async (businessData: any) => {
  try {
    // Get existing cache to compare what's new
    const existingCache = await getUniversalAICache();
    
    // Find only new or updated business data with content deduplication
    const newOrUpdatedData: any = {};
    Object.entries(businessData).forEach(([hash, data]) => {
      const existing = (existingCache as any)[hash];
      const newData = data as any;
      
      // Create deterministic content hash (normalized, stable fields only)
      const normalizedData = {
        name: (newData.name || '').toLowerCase().trim(),
        address: (newData.address || '').toLowerCase().trim(),
        normalizedCategory: (newData.normalizedCategory || '').toLowerCase().trim(),
        website: (newData.website || '').toLowerCase().trim(),
        phone: (newData.phone || '').toLowerCase().trim(),
        rating: newData.rating || 0,
        reviewCount: newData.reviewCount || 0,
        businessStatus: (newData.businessStatus || '').toLowerCase().trim(),
        modelVersion: (newData.modelVersion || '').toString().trim()
        // Removed 'types' as it's unstable (order can change)
      };
      
      const contentHash = JSON.stringify(normalizedData);
      
      // Check if this is new data or if the content has actually changed
      const existingHash = existing?.contentHash;
      const hasMeaningfulChange = !existing || 
        existingHash !== contentHash ||
        Math.abs((existing.rating || 0) - (newData.rating || 0)) >= 0.1 ||
        Math.abs((existing.reviewCount || 0) - (newData.reviewCount || 0)) >= 10;
      
      // Add cooldown: skip if updated recently (unless meaningful change)
      const lastUpdate = existing?.updatedAt?.toDate?.() || new Date(0);
      const timeSinceUpdate = Date.now() - lastUpdate.getTime();
      const cooldownPeriod = 120000; // 120 seconds
      
      if (hasMeaningfulChange && (timeSinceUpdate > cooldownPeriod || !existing)) {
        newOrUpdatedData[hash] = {
          ...(data as object),
          contentHash // Add content hash for future deduplication
        };
      }
    });
    
    // If no new data, skip the Firebase write
    if (Object.keys(newOrUpdatedData).length === 0) {
      console.log('✅ No new business data to save to Firebase');
      return;
    }
    
    // Save only new/updated data in smaller batches with pacing
    const batchSize = 20;
    const entries = Object.entries(newOrUpdatedData);
    
    for (let i = 0; i < entries.length; i += batchSize) {
      const batchEntries = entries.slice(i, i + batchSize);
      
      // Commit batch with retry logic for resource-exhausted errors
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          // Create a NEW batch for each attempt (GPT-5's key fix)
          const batch = writeBatch(db);
          
          batchEntries.forEach(([hash, data]) => {
            const docRef = doc(db, 'universal', 'ai_cache', 'businesses', hash);
            
            // Filter out undefined values before saving to Firebase
            const cleanData = Object.fromEntries(
              Object.entries(data as object).filter(([key, value]) => value !== undefined)
            );
            
            batch.set(docRef, {
              ...cleanData,
              updatedAt: new Date()
            });
          });
          
          await batch.commit();
          console.log(`✅ Saved batch ${Math.floor(i/batchSize) + 1} to Firebase (${batchEntries.length} businesses)`);
          break; // Success, exit retry loop
        } catch (error: any) {
          retryCount++;
          
          // Check if it's a resource-exhausted error
          if (error.message && error.message.includes('resource-exhausted')) {
            // Longer backoff with jitter: 1000ms → 2000ms → 4000ms ±20%
            const baseDelay = 1000 * Math.pow(2, retryCount - 1);
            const jitter = baseDelay * 0.2 * (Math.random() - 0.5); // ±20% jitter
            const delay = Math.min(baseDelay + jitter, 4000);
            console.log(`⏳ Rate limited, retrying batch ${Math.floor(i/batchSize) + 1} in ${Math.round(delay)}ms (attempt ${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            // Not a rate limit error, don't retry
            throw error;
          }
        }
      }
      
      // Add longer pacing delay between batches (1000-1500ms with jitter)
      if (i + batchSize < entries.length) {
        const pacingDelay = 1000 + Math.random() * 500; // 1000-1500ms with jitter
        await new Promise(resolve => setTimeout(resolve, pacingDelay));
      }
    }
    
    console.log(`✅ Universal AI cache updated: ${Object.keys(newOrUpdatedData).length} new/updated businesses`);
  } catch (error: any) {
    console.error('Error saving universal AI cache:', error);
    
    // Check if the error is due to missing collection structure
    if (error.message && error.message.includes('collection')) {
      console.log('🔄 Attempting to create missing collection structure...');
      try {
        // Create the collection structure by adding a dummy document
        const dummyDocRef = doc(db, 'universal', 'ai_cache', 'businesses', '_init');
        await setDoc(dummyDocRef, { 
          _created: new Date(),
          _note: 'Collection structure initialization'
        });
        console.log('✅ Created missing collection structure');
        
        // Now try to save the actual data again (recursive call)
        await performSaveUniversalAICache(businessData);
        
        // Clean up the dummy document
        await deleteDoc(dummyDocRef);
        
      } catch (retryError: any) {
        console.error('Failed to create collection structure:', retryError);
        console.warn('⚠️ Firebase cache save failed - continuing with local cache only');
      }
    } else {
      console.warn('⚠️ Firebase cache save failed - continuing with local cache only');
    }
  }
};

export const getUniversalAICache = async () => {
  try {
    const cacheQuery = query(collection(db, 'universal', 'ai_cache', 'businesses'));
    const snapshot = await getDocs(cacheQuery);
    
    const cacheData: any = {};
    snapshot.forEach(doc => {
      // Skip the initialization document
      if (doc.id !== '_init') {
        cacheData[doc.id] = doc.data();
      }
    });
    
    console.log(`📦 Loaded ${Object.keys(cacheData).length} business data entries from universal cache`);
    return cacheData;
  } catch (error: any) {
    console.error('Error loading universal AI cache:', error);
    console.warn('⚠️ Firebase cache collection may not exist yet - starting with empty cache');
    return {};
  }
};

// ===== INCREMENTAL BUSINESS FETCH + BROWSER-SIDE CACHE (to reduce reads) =====

type LocalBusinessCacheEntry = { data: any; ts: number };
type LocalBusinessCache = { entries: Record<string, LocalBusinessCacheEntry> };

const LOCAL_BUSINESS_CACHE_KEY = 'raedar_business_cache_v1';

const loadLocalBusinessCache = (): LocalBusinessCache => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_BUSINESS_CACHE_KEY) || '{"entries":{}}');
  } catch {
    return { entries: {} };
  }
};

const saveLocalBusinessCache = (bag: LocalBusinessCache) => {
  const keys = Object.keys(bag.entries);
  if (keys.length > 1000) {
    // Hard cap at 1000 entries; drop oldest
    keys.sort((a, b) => (bag.entries[a].ts - bag.entries[b].ts));
    for (let i = 0; i < keys.length - 1000; i++) delete bag.entries[keys[i]];
  }
  localStorage.setItem(LOCAL_BUSINESS_CACHE_KEY, JSON.stringify(bag));
};

// Fetch businesses by hashes in chunks of 10 using documentId() IN queries
export const getBusinessesByHashes = async (hashes: string[]): Promise<Record<string, any>> => {
  const unique = Array.from(new Set(hashes)).filter(Boolean);
  const out: Record<string, any> = {};
  for (let i = 0; i < unique.length; i += 10) {
    const chunk = unique.slice(i, i + 10);
    const q = query(
      collection(db, 'universal', 'ai_cache', 'businesses'),
      where(documentId(), 'in', chunk)
    );
    const snap = await getDocs(q);
    snap.forEach(d => {
      out[d.id] = d.data();
    });
  }
  return out;
};

// Read-through cache: return requested hashes from local cache, fetch missing, store with TTL
export const getBusinessesCached = async (
  hashes: string[],
  ttlMs: number = 6 * 60 * 60 * 1000
): Promise<Record<string, any>> => {
  const now = Date.now();
  const bag = loadLocalBusinessCache();
  const missing: string[] = [];

  hashes.forEach(h => {
    const e = bag.entries[h];
    if (!e || (now - e.ts) > ttlMs) missing.push(h);
  });

  if (missing.length > 0) {
    try {
      const fetched = await getBusinessesByHashes(missing);
      Object.entries(fetched).forEach(([id, data]) => {
        bag.entries[id] = { data, ts: now };
      });
      saveLocalBusinessCache(bag);
    } catch (e) {
      console.warn('Incremental business fetch failed:', e);
    }
  }

  const out: Record<string, any> = {};
  hashes.forEach(h => {
    const e = bag.entries[h];
    if (e) out[h] = e.data;
  });
  return out;
};

export const clearUniversalAICache = async () => {
  try {
    // Clear business-data cache
    const cacheQuery = query(collection(db, 'universal', 'ai_cache', 'businesses'));
    const snapshot = await getDocs(cacheQuery);
    if (!snapshot.empty) {
      const batch = writeBatch(db);
      snapshot.forEach(doc => {
        if (doc.id !== '_init') {
          batch.delete(doc.ref);
        }
      });
      await batch.commit();
    }
    
    // Clear analysis cache
    const analysisQuery = query(collection(db, 'universal', 'ai_analysis', 'businesses'));
    const analysisSnap = await getDocs(analysisQuery);
    if (!analysisSnap.empty) {
      const batch2 = writeBatch(db);
      analysisSnap.forEach(doc => {
        if (doc.id !== '_init') {
          batch2.delete(doc.ref);
        }
      });
      await batch2.commit();
    }
    
    console.log('🗑️ Universal AI cache (business data + analysis) cleared from Firebase');
  } catch (error: any) {
    console.error('Error clearing universal AI cache:', error);
    console.warn('⚠️ Firebase cache collection may not exist - nothing to clear');
  }
};

// ===== UNIVERSAL AI ANALYSIS (OUTPUT) CACHE =====
export const getUniversalAIAnalysis = async (hash: string) => {
  try {
    const docRef = doc(db, 'universal', 'ai_analysis', 'businesses', hash);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() : null;
  } catch (error: any) {
    console.error('Error getting analysis from universal cache:', error);
    return null;
  }
};

export const saveUniversalAIAnalyses = async (analyses: Record<string, any>) => {
  return withRateLimitProtection(async () => {
    return withRetryBackoff(async () => {
      try {
        const entries = Object.entries(analyses);
        const batchSize = 20;
        for (let i = 0; i < entries.length; i += batchSize) {
          const batch = writeBatch(db);
          const slice = entries.slice(i, i + batchSize);
          slice.forEach(([hash, data]) => {
            const ref = doc(db, 'universal', 'ai_analysis', 'businesses', hash);
            const clean = Object.fromEntries(Object.entries(data as object).filter(([_, v]) => v !== undefined));
            batch.set(ref, { ...clean, updatedAt: new Date() });
          });
          await batch.commit();
        }
        console.log(`✅ Saved ${entries.length} analysis entries to universal cache`);
      } catch (error: any) {
        console.error('Error saving universal AI analyses:', error);
      }
    });
  });
};

// ===== CURRENT ADVERTISER MANAGEMENT =====
// Store current advertisers in a single document that all users share
// All users can mark businesses, only admins can unmark them
export const saveCurrentAdvertisers = async (
  advertisers: string[],
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  return withRateLimitProtection(async () => {
    return withRetryBackoff(async () => {
      await setDoc(doc(db, 'universal', universalOrgDocId('current_advertisers', orgKey)), {
        advertisers,
        orgKey,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.uid || 'unknown'
      });
      console.log(`✅ Current advertisers saved (${orgKey}):`, advertisers.length, 'items');
    });
  });
};

export const getCurrentAdvertisers = async (orgKey: OrganizationKey = DEFAULT_ORGANIZATION) => {
  try {
    const scopedDoc = await getDoc(doc(db, 'universal', universalOrgDocId('current_advertisers', orgKey)));
    if (scopedDoc.exists()) {
      return scopedDoc.data().advertisers || [];
    }
    if (orgKey === DEFAULT_ORGANIZATION) {
      const legacyDoc = await getDoc(doc(db, 'universal', 'current_advertisers'));
      if (legacyDoc.exists()) {
        return legacyDoc.data().advertisers || [];
      }
    }
    return [];
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Subscribe to org-scoped current advertisers in real-time
export const subscribeCurrentAdvertisers = (
  onChange: (entries: string[]) => void,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  const ref = doc(db, 'universal', universalOrgDocId('current_advertisers', orgKey));
  let legacyLoaded = false;
  return onSnapshot(ref, async (snap) => {
    const data = snap.data() as any;
    let list = Array.isArray(data?.advertisers) ? data.advertisers : [];
    if (!list.length && orgKey === DEFAULT_ORGANIZATION && !legacyLoaded) {
      legacyLoaded = true;
      try {
        const legacyDoc = await getDoc(doc(db, 'universal', 'current_advertisers'));
        if (legacyDoc.exists()) {
          list = legacyDoc.data().advertisers || [];
        }
      } catch {
        list = [];
      }
    }
    onChange(list);
  }, (error) => {
    console.error('Current advertisers snapshot error:', error);
  });
};

export const addCurrentAdvertiser = async (
  businessName: string,
  businessAddress: string,
  businessHash: string,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Get user profile for display name
    const userProfile = await getUserProfile(currentUser.uid);
    const displayName = userProfile?.name || currentUser.email || 'Unknown User';

    // Create advertiser entry: "Business Name|User UID|User Name|Date|Business Hash"
    const advertiserEntry = `${businessName}|${currentUser.uid}|${displayName}|${new Date().toISOString()}|${businessHash}`;

    // Get existing advertisers
    const existingAdvertisers = await getCurrentAdvertisers(orgKey);
    
    // Check if already marked for this business (single active mark)
    const alreadyMarked = existingAdvertisers.some((entry: string) => entry.split('|')[4] === businessHash);
    if (alreadyMarked) {
      console.log('Business already marked as current advertiser:', businessName);
      return;
    }

    // Add new advertiser
    const updatedAdvertisers = [...existingAdvertisers, advertiserEntry];
    await saveCurrentAdvertisers(updatedAdvertisers, orgKey);
    
    console.log('✅ Marked as current advertiser:', businessName);
  } catch (error: any) {
    console.error('Error marking as current advertiser:', error);
    throw new Error(error.message);
  }
};

export const removeCurrentAdvertiser = async (
  businessHash: string,
  orgKey: OrganizationKey = DEFAULT_ORGANIZATION
) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin
    const userProfile = await getUserProfile(currentUser.uid);
    const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'SuperAdmin';

    if (!isAdmin) {
      throw new Error('Only admins can remove current advertisers');
    }

    // Get existing advertisers
    const existingAdvertisers = await getCurrentAdvertisers(orgKey);
    
    // Remove rules:
    // - Admin: remove any mark for this business hash
    // - Non-admin: remove only if the mark was created by this user
    const updatedAdvertisers = existingAdvertisers.filter((entry: string) => {
      const parts = entry.split('|');
      const entryUid = parts[1];
      const entryHash = parts[4];
      if (entryHash !== businessHash) return true; // keep other businesses
      // when hashes match, keep if user is not allowed to remove
      if (!isAdmin && entryUid !== currentUser.uid) return true;
      return false; // drop the removable entry
    });
    await saveCurrentAdvertisers(updatedAdvertisers, orgKey);
    
    console.log('✅ Removed current advertiser:', businessHash);
  } catch (error: any) {
    console.error('Error removing current advertiser:', error);
    throw new Error(error.message);
  }
};

export const isCurrentAdvertiser = async (businessHash: string) => {
  try {
    const advertisers = await getCurrentAdvertisers();
    const advertiserEntry = advertisers.find((entry: string) => entry.split('|')[4] === businessHash);
    
    if (advertiserEntry) {
      // Parse entry: "Business Name|User UID|User Name|Date|Business Hash"
      const parts = advertiserEntry.split('|');
      return {
        businessName: parts[0],
        markedByName: parts[2],
        markedAt: new Date(parts[3]),
        businessHash: parts[4]
      };
    }
    return null;
  } catch (error: any) {
    console.error('Error checking current advertiser status:', error);
    return null;
  }
};

// ===== INVITATION SYSTEM =====
export const generateInvitation = async (invitedBy: string, invitedEmail?: string) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin
    const userProfile = await getUserProfile(currentUser.uid);
    const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'SuperAdmin';

    if (!isAdmin) {
      throw new Error('Only admins can generate invitations');
    }

    // Generate a unique invitation code
    const invitationCode = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    
    // Set expiration date (7 days from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    // Save invitation to Firestore
    await addDoc(collection(db, 'invitations'), {
      code: invitationCode,
      invitedBy: invitedBy,
      invitedEmail: invitedEmail || '',
      createdAt: new Date(),
      expiresAt: expiresAt,
      used: false,
      usedBy: '',
      usedAt: null
    });

    console.log('✅ Invitation generated:', invitationCode);
    return invitationCode;
  } catch (error: any) {
    console.error('Error generating invitation:', error);
    throw new Error(error.message);
  }
};

export const validateInvitation = async (invitationCode: string) => {
  try {
    // Find invitation by code
    const invitationsQuery = query(
      collection(db, 'invitations'),
      where('code', '==', invitationCode)
    );
    const invitationsSnapshot = await getDocs(invitationsQuery);

    if (invitationsSnapshot.empty) {
      return { valid: false, message: 'Invalid invitation code' };
    }

    const invitation = invitationsSnapshot.docs[0].data();
    const invitationId = invitationsSnapshot.docs[0].id;

    // Check if already used
    if (invitation.used) {
      return { valid: false, message: 'Invitation code has already been used' };
    }

    // Check if expired
    const now = new Date();
    const expiresAt = invitation.expiresAt.toDate();
    if (now > expiresAt) {
      return { valid: false, message: 'Invitation code has expired' };
    }

    return { valid: true, invitationId, invitation };
  } catch (error: any) {
    console.error('Error validating invitation:', error);
    return { valid: false, message: 'Error validating invitation code' };
  }
};

export const markInvitationAsUsed = async (invitationCode: string, usedBy: string) => {
  try {
    // Find invitation by code
    const invitationsQuery = query(
      collection(db, 'invitations'),
      where('code', '==', invitationCode)
    );
    const invitationsSnapshot = await getDocs(invitationsQuery);

    if (invitationsSnapshot.empty) {
      throw new Error('Invitation not found');
    }

    const invitationDoc = invitationsSnapshot.docs[0];
    
    // Mark as used
    await updateDoc(invitationDoc.ref, {
      used: true,
      usedBy: usedBy,
      usedAt: new Date()
    });

    console.log('✅ Invitation marked as used:', invitationCode);
  } catch (error: any) {
    console.error('Error marking invitation as used:', error);
    throw new Error(error.message);
  }
};

export const getInvitations = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin
    const userProfile = await getUserProfile(currentUser.uid);
    const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'SuperAdmin';

    if (!isAdmin) {
      throw new Error('Only admins can view invitations');
    }

    const invitationsQuery = query(collection(db, 'invitations'));
    const invitationsSnapshot = await getDocs(invitationsQuery);

    const invitations = invitationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return invitations;
  } catch (error: any) {
    console.error('Error getting invitations:', error);
    throw new Error(error.message);
  }
};

export const deleteInvitation = async (invitationId: string) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin
    const userProfile = await getUserProfile(currentUser.uid);
    const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'SuperAdmin';

    if (!isAdmin) {
      throw new Error('Only admins can delete invitations');
    }

    await deleteDoc(doc(db, 'invitations', invitationId));
    console.log('✅ Invitation deleted:', invitationId);
  } catch (error: any) {
    console.error('Error deleting invitation:', error);
    throw new Error(error.message);
  }
};

// ===== GLOBAL FIREBASE RATE LIMITING =====
// Prevent all Firebase operations from hitting rate limits

// Global write queue to prevent concurrent Firebase operations
let isFirebaseWriteInProgress = false;
const firebaseWriteQueue: Array<() => Promise<void>> = [];

const processFirebaseWriteQueue = async () => {
  if (isFirebaseWriteInProgress || firebaseWriteQueue.length === 0) return;
  
  isFirebaseWriteInProgress = true;
  while (firebaseWriteQueue.length > 0) {
    const writeOperation = firebaseWriteQueue.shift();
    if (writeOperation) {
      try {
        await writeOperation();
        // Add delay between operations to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300));
      } catch (error) {
        console.error('Error in Firebase write queue operation:', error);
      }
    }
  }
  isFirebaseWriteInProgress = false;
};

// Wrapper function for all Firebase write operations
const withRateLimitProtection = async <T>(operation: () => Promise<T>): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    firebaseWriteQueue.push(async () => {
      try {
        const result = await operation();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    processFirebaseWriteQueue();
  });
};

// Retry wrapper with exponential backoff for resource-exhausted errors
const withRetryBackoff = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      return await operation();
    } catch (error: any) {
      retryCount++;
      
      // Check if it's a resource-exhausted error
      if (error.message && error.message.includes('resource-exhausted')) {
        if (retryCount >= maxRetries) {
          throw error; // Give up after max retries
        }
        
        // Exponential backoff with jitter: 1000ms → 2000ms → 4000ms ±20%
        const baseDelay = 1000 * Math.pow(2, retryCount - 1);
        const jitter = baseDelay * 0.2 * (Math.random() - 0.5);
        const delay = Math.min(baseDelay + jitter, 4000);
        
        console.log(`⏳ Firebase rate limited, retrying in ${Math.round(delay)}ms (attempt ${retryCount}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // Not a rate limit error, don't retry
        throw error;
      }
    }
  }
  
  throw new Error('Max retries exceeded');
};
